import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Phone, ArrowRight, ChevronLeft, ChevronRight as ChevronRightIcon,
  Clock, Award, ShieldCheck, Heart, MapPin, Star,
} from "lucide-react";
import { companyInfo, services as mockServicesList, whyChooseUs, galleryImages, googleProfile, testimonials } from "../data/mock";
import { useCompany, useServices } from "../hooks/useContent";

const iconMap = {
  Clock, Award, ShieldCheck, Heart, MapPin, Star,
  HandHeart: Heart, Church: Star, FileText: Heart, Car: Heart, CreditCard: Heart,
  Truck: Heart, Globe: Heart, FileCheck: Heart, Sparkles: Heart,
  Flame: Heart, Package: Heart, HelpCircle: Heart, Route: Heart, ClipboardList: Heart, Flower2: Heart, Candle: Clock,
};

const heroSlides = [
  { image: galleryImages.celestial, alt: "Servicii funerare Constanța – Casa Funerară Sfântul Andrei" },
  { image: galleryImages.churchInterior, alt: "Interior biserică ortodoxă – organizare ceremonii funerare Constanța" },
  { image: galleryImages.flowers1, alt: "Coroane funerare și aranjamente florale – Casa Funerară Sfântul Andrei Constanța" },
  { image: galleryImages.sfAndreiBiserica, alt: "Sfântul Apostol Andrei – patronul Casei Funerare Sfântul Andrei" },
  { image: galleryImages.waxCandle, alt: "Lumânări tradiționale funerare – servicii complete în Constanța" },
];

const heroMessages = [
  { headline: "Servicii funerare complete în Constanța", sub: "NON-STOP 24/7" },
  { headline: "Preluare rapidă de la domiciliu, spital, IML sau azil", sub: "Intervenim prompt, cu respect și discreție" },
  { headline: "Transport funerar și organizare ceremonie", sub: "Cu respect și discreție" },
  { headline: "Pachete funerare personalizate", sub: "Consultanță pentru acte și formalități" },
];

export default function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentMsg, setCurrentMsg] = useState(0);
  const [paused, setPaused] = useState(false);

  const { data: companyData } = useCompany();
  const companyInfo = companyData?.companyInfo || {};
  const whyChooseUs = companyData?.whyChooseUs || [];
  const googleProfile = companyData?.googleProfile || { reviews: [] };
  const testimonials = companyData?.testimonials || [];
  const { data: services } = useServices();

  const nextSlide = useCallback(() => {
    setCurrentSlide(p => (p + 1) % heroSlides.length);
  }, []);
  const prevSlide = useCallback(() => {
    setCurrentSlide(p => (p - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(nextSlide, 5000);
    return () => clearInterval(t);
  }, [paused, nextSlide]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setCurrentMsg(p => (p + 1) % heroMessages.length), 4500);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <div>
      <Helmet>
        <title>Casa Funerară Sfântul Andrei Constanța – Servicii Funerare NON-STOP 24/7</title>
        <meta name="description" content="Servicii funerare complete în Constanța: preluare NON-STOP 24/7, transport funerar, organizare înmormântare, pachete personalizate. Casa Funerară Sfântul Andrei – 10 ani experiență." />
        <link rel="canonical" href="https://funeraresfantulandrei.ro/" />
        <meta property="og:title" content="Casa Funerară Sfântul Andrei Constanța – Servicii Funerare NON-STOP" />
        <meta property="og:description" content="Servicii funerare complete în Constanța. Preluare NON-STOP 24/7, transport funerar, organizare înmormântare." />
        <meta property="og:url" content="https://funeraresfantulandrei.ro/" />
      </Helmet>
      {/* ═══ Hero Carousel ═══ */}
      <section
        className="hero-section relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        data-testid="hero-section"
      >
        {heroSlides.map((slide, i) => (
          <div key={i} className={`hero-slide ${i === currentSlide ? 'active' : ''}`}>
            <img src={slide.image} alt={slide.alt} style={{ objectPosition: "center 35%" }} loading={i === 0 ? "eager" : "lazy"} fetchpriority={i === 0 ? "high" : "auto"} />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/75" />
          </div>
        ))}
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8 w-full relative z-10 text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-5 py-2 rounded-full" style={{ background: "rgba(198,168,90,0.15)", border: "1px solid rgba(198,168,90,0.3)" }}>
            <Clock className="w-3.5 h-3.5" style={{ color: "var(--gold)" }} />
            <span className="text-xs uppercase tracking-[3px] font-medium" style={{ color: "var(--gold)" }}>
              Disponibil NON-STOP 24/7
            </span>
          </div>
          <div className="min-h-[140px] sm:min-h-[160px] flex flex-col items-center justify-center">
            <h1
              key={currentMsg}
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-semibold text-white leading-[1.15] mb-4 animate-text-slide max-w-4xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="hero-heading"
            >
              {heroMessages[currentMsg].headline}
            </h1>
            <p
              key={`sub-${currentMsg}`}
              className="text-base sm:text-lg text-white/55 leading-relaxed animate-text-slide max-w-xl"
              style={{ animationDelay: "0.15s" }}
            >
              {heroMessages[currentMsg].sub}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <a
              href={`tel:${companyInfo?.phone || ""}`}
              className="flex items-center gap-3 px-8 py-4 text-sm font-semibold tracking-wide no-underline transition-all hover:shadow-lg"
              style={{ borderRadius: "var(--radius-pill)", backgroundColor: "var(--gold)", color: "var(--black)" }}
              data-testid="hero-cta-phone"
            >
              <Phone className="w-4 h-4" />
              {companyInfo?.phone || ""}
            </a>
            <a
              href="/contact"
              className="flex items-center gap-2 px-7 py-4 text-sm font-medium tracking-wide no-underline transition-all"
              style={{ borderRadius: "var(--radius-pill)", border: "1.5px solid var(--gold)", color: "var(--gold)" }}
              data-testid="hero-cta-offer"
            >
              Solicită o ofertă
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
        {/* Carousel controls */}
        <div className="absolute bottom-8 left-0 right-0 z-10 flex items-center justify-center gap-8">
          <button onClick={prevSlide} className="hero-arrow" aria-label="Imagine anterioară">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            {heroSlides.map((_, i) => (
              <button key={i} onClick={() => setCurrentSlide(i)} className={`hero-dot ${i === currentSlide ? 'active' : ''}`} aria-label={`Imagine ${i + 1}`} />
            ))}
          </div>
          <button onClick={nextSlide} className="hero-arrow" aria-label="Imagine următoare">
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#0f0f0f] to-transparent" />
      </section>

      {/* ═══ Trust Indicators ═══ */}
      <section className="pt-12 pb-8 sm:py-10" style={{ backgroundColor: "var(--black)" }} data-testid="trust-indicators">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Ani Experiență", value: "10+" },
              { label: "Disponibilitate", value: "24/7" },
              { label: "Rating Google", value: "5.0" },
              { label: "Încredere Familii", value: "100%" },
            ].map((item, i) => (
              <div key={i} className="text-center py-4">
                <p className="text-3xl font-semibold mb-1" style={{ fontFamily: "'Playfair Display', serif", color: "var(--gold)" }}>
                  {item.value}
                </p>
                <p className="text-xs uppercase tracking-[2px] text-white/50">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Google Reviews CTA Section ═══ */}
      <section className="section-spacing" style={{ backgroundColor: "var(--black)" }} data-testid="google-reviews-section">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">

            {/* Image — left on desktop, top on mobile */}
            <div className="w-full md:w-5/12 shrink-0">
              <div className="rounded-2xl overflow-hidden" style={{ boxShadow: "0 8px 40px rgba(0,0,0,0.45)" }}>
                <img
                  src="/images/google-reviews.jpg"
                  alt="Google Reviews 5 stele – Casa Funerară Sfântul Andrei Constanța"
                  className="w-full h-72 md:h-96 object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Text — right on desktop, bottom on mobile */}
            <div className="w-full md:w-7/12 text-center md:text-left">
              {/* Stars */}
              <div className="flex items-center justify-center md:justify-start gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5" fill="#C6A85A" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
                <span className="text-white/40 text-sm ml-2">5.0 pe Google</span>
              </div>

              <h2
                className="text-2xl sm:text-3xl font-semibold mb-5 leading-snug"
                style={{ fontFamily: "'Playfair Display', serif", color: "#fff" }}
              >
                Părerea dumneavoastră{" "}
                <span style={{ color: "var(--gold)" }}>contează</span>
              </h2>

              <p className="text-base leading-relaxed mb-8 max-w-lg mx-auto md:mx-0" style={{ color: "rgba(255,255,255,0.60)" }}>
                Dacă ați apelat la serviciile noastre, vă rugăm să ne lăsați o recenzie pe Google.
                Sprijinul dumneavoastră ajută și alte familii din Constanța să aleagă cu încredere.
              </p>

              <a
                href="https://g.page/r/CRbaHIZUjJVVEBM/review"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 font-semibold no-underline transition-all duration-200 hover:opacity-90 hover:scale-105 active:scale-95"
                style={{
                  backgroundColor: "var(--gold)",
                  color: "#0f0f0f",
                  borderRadius: "14px",
                  minHeight: "52px",
                  padding: "14px 32px",
                  fontSize: "15px",
                  letterSpacing: "0.02em",
                }}
                data-testid="google-review-section-btn"
              >
                {/* Google G icon */}
                <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Lasă un Google Review
              </a>
            </div>

          </div>
        </div>
      </section>

      {/* ═══ Services Preview ═══ */}
      <section className="section-spacing" style={{ backgroundColor: "var(--warm-bg)" }} data-testid="services-section">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8 text-center">
          <span className="text-xs uppercase tracking-[4px] font-medium" style={{ color: "var(--gold)" }}>Servicii</span>
          <h2 className="text-2xl sm:text-3xl font-semibold mt-4 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Servicii Funerare Complete
          </h2>
          <p className="leading-relaxed max-w-2xl mx-auto mb-14" style={{ color: "var(--text-secondary)" }}>
            Oferim o gamă completă de servicii funerare în Constanța și Lumina,
            cu atenție la fiecare detaliu și respect pentru tradițiile familiei.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
            {[
              { slug: "servicii-complete-inmormantare-constanta", img: galleryImages.churchInterior, alt: "Servicii complete înmormântare Constanța – Casa Funerară Sfântul Andrei" },
              { slug: "transport-funerar-local", img: galleryImages.van, alt: "Transport funerar autorizat Constanța – Casa Funerară Sfântul Andrei" },
              { slug: "transport-funerar-national", img: galleryImages.mercedes, alt: "Transport funerar național din Constanța – vehicule autorizate" },
              { slug: "transport-funerar-international", img: galleryImages.vanBranding, alt: "Transport funerar internațional și repatriere Constanța" },
              { slug: "aranjamente-florale-funerare", img: galleryImages.flowers1, alt: "Coroane și aranjamente florale funerare Constanța" },
              { slug: "parastas-pomeniri", img: galleryImages.parastasPackages, alt: "Organizare parastas și pomeniri Constanța – pachete complete" },
            ].map(({ slug, img, alt }) => {
              const service = (Array.isArray(services) ? services : []).find(s => s.slug === slug);
              if (!service) return null;
              return (
                <Link key={slug} to={`/servicii/${slug}`} className="group no-underline block rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl text-left" style={{ border: "1px solid var(--warm-border)", backgroundColor: "#fff" }}>
                  <div className="relative h-44 overflow-hidden">
                    <img src={img} alt={alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </div>
                  <div className="p-5">
                    <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif", color: "var(--black)" }}>
                      {service.shortTitle}
                    </h3>
                    <p className="text-xs leading-relaxed line-clamp-2 mb-4" style={{ color: "var(--text-muted)" }}>
                      {service.intro.substring(0, 90)}...
                    </p>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider group-hover:gap-2 transition-all duration-300" style={{ color: "var(--gold)" }}>
                      Detalii <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="mt-12">
            <Link
              to="/servicii"
              className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide no-underline hover:gap-3 transition-all duration-300 px-7 py-3"
              style={{ color: "var(--gold)", border: "1.5px solid var(--gold)", borderRadius: "var(--radius-pill)" }}
            >
              Vezi Toate Serviciile
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ Why Choose Us ═══ */}
      <section className="section-spacing bg-white" data-testid="why-choose-us">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8 text-center">
          <span className="text-xs uppercase tracking-[4px] font-medium" style={{ color: "var(--gold)" }}>De ce noi</span>
          <h2 className="text-2xl sm:text-3xl font-semibold mt-4 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            De Ce Să Ne Alegeți
          </h2>
          <p className="leading-relaxed max-w-2xl mx-auto mb-14" style={{ color: "var(--text-secondary)" }}>
            Cu peste 10 ani de experiență, oferim servicii funerare de cea mai
            înaltă calitate, cu empatie și profesionalism.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 stagger-children">
            {(Array.isArray(whyChooseUs) ? whyChooseUs : []).map((item, i) => {
              const Icon = iconMap[item.icon] || Heart;
              return (
                <div key={i} className="text-center group p-6 rounded-2xl hover:bg-[var(--warm-bg)] transition-all duration-400">
                  <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-2xl transition-all duration-400" style={{ backgroundColor: "var(--gold-bg)" }}>
                    <Icon className="w-5 h-5 group-hover:scale-110 transition-transform duration-400" style={{ color: "var(--gold)" }} />
                  </div>
                  <h3 className="text-base font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--text-muted)" }}>{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ Gallery ═══ */}
      <section className="section-spacing" style={{ backgroundColor: "var(--warm-bg)" }} data-testid="gallery-section">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8 text-center">
          <span className="text-xs uppercase tracking-[4px] font-medium" style={{ color: "var(--gold)" }}>Galerie</span>
          <h2 className="text-2xl sm:text-3xl font-semibold mt-4 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Din Activitatea Noastră
          </h2>
          <p className="leading-relaxed max-w-2xl mx-auto mb-14" style={{ color: "var(--text-secondary)" }}>
            Imagini din activitatea zilnică a Casei Funerare Sfântul Andrei – de la aranjamente florale
            la transportul funerar autorizat.
          </p>

          {/* Transport Fleet Row */}
          <div className="mb-4">
            <p className="text-xs uppercase tracking-[3px] font-medium mb-5 text-left" style={{ color: "var(--text-muted)" }}>Flota de Transport</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
              {[
                { src: galleryImages.van, alt: "Vehicul funerar autorizat Casa Funerară Sfântul Andrei – transport în Constanța" },
                { src: galleryImages.mercedes, alt: "Mașină funerară Mercedes – transport funerar național și internațional din Constanța" },
                { src: galleryImages.vanBranding, alt: "Autovehicul funerar branduit Casa Funerară Sfântul Andrei Constanța" },
                { src: galleryImages.opelVanFront, alt: "Opel funerar autorizat – preluare imediată domiciliu, spital, IML Constanța" },
              ].map((img, i) => (
                <div key={i} className="img-hover-zoom rounded-xl overflow-hidden aspect-[4/3]" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
                  <img src={img.src} alt={img.alt} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          </div>

          {/* General Gallery Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { src: galleryImages.showroom, alt: "Showroom sicrie și coroane – Casa Funerară Sfântul Andrei Constanța" },
              { src: galleryImages.tricolorWreath, alt: "Coroană funerară tricolor – aranjamente florale funerare Constanța" },
              { src: galleryImages.parastasPackages, alt: "Pachete tradiționale parastas – organizare pomeniri Constanța" },
              { src: galleryImages.showroomCoffinsWreaths, alt: "Sicrie și coroane funerare în showroom – Casa Funerară Sfântul Andrei" },
              { src: galleryImages.coliva, alt: "Colivă tradițională de pomenire – tradiții funerare ortodoxe" },
              { src: galleryImages.waxCandle, alt: "Lumânare din ceară – accesorii funerare tradiționale Constanța" },
            ].map((img, i) => (
              <div key={i} className="img-hover-zoom rounded-xl overflow-hidden aspect-[4/3]" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Google Reviews ═══ */}
      <section className="section-spacing bg-white" data-testid="reviews-section">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8 text-center">
          <span className="text-xs uppercase tracking-[4px] font-medium" style={{ color: "var(--gold)" }}>Recenzii Google</span>
          <h2 className="text-2xl sm:text-3xl font-semibold mt-4 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Ce Spun Familiile
          </h2>
          <div className="inline-flex items-center gap-4 px-8 py-4 rounded-2xl mb-14" style={{ backgroundColor: "var(--gold-bg)", border: "1px solid var(--warm-border)" }}>
            <p className="text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{googleProfile.rating}</p>
            <div className="w-px h-10" style={{ backgroundColor: "var(--warm-border)" }} />
            <div>
              <div className="flex items-center gap-1 mb-1">
                {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4" style={{ color: "var(--gold)", fill: "var(--gold)" }} />)}
              </div>
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>{googleProfile.totalReviews} recenzii pe Google</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children text-left">
            {(Array.isArray(googleProfile?.reviews) ? googleProfile.reviews : []).map((review, i) => (
              <div key={i} className="p-7 rounded-2xl border hover:shadow-md transition-all duration-400" style={{ borderColor: "var(--warm-border)" }}>
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-3.5 h-3.5" style={{ color: "var(--gold)", fill: "var(--gold)" }} />)}
                </div>
                <p className="text-[15px] leading-relaxed mb-6" style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", color: "var(--text-secondary)" }}>
                  „{review.text}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold">{review.author}</p>
                    <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{review.date}</p>
                  </div>
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full" style={{ color: "var(--gold)", backgroundColor: "var(--gold-bg)" }}>
                    {review.source}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {/* Awards */}
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6 text-left">
            <div className="flex items-center gap-5 p-6 rounded-2xl" style={{ backgroundColor: "var(--gold-bg)", border: "1px solid var(--warm-border)" }}>
              <img src={galleryImages.soimiiAward} alt="Premiu Șoimii Funerari 2023 – servicii funerare Constanța" className="w-20 h-20 object-cover rounded-xl flex-shrink-0" loading="lazy" />
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Award className="w-4 h-4" style={{ color: "var(--gold)" }} />
                  <p className="text-sm font-semibold">Laureatul Proiectului Șoimii Funerari</p>
                </div>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>2023 – Loc 3, Lider în Industrie, Constanța</p>
              </div>
            </div>
            <div className="flex items-center gap-5 p-6 rounded-2xl" style={{ backgroundColor: "var(--gold-bg)", border: "1px solid var(--warm-border)" }}>
              <div className="flex items-center gap-3">
                <Star className="w-5 h-5" style={{ color: "var(--gold)", fill: "var(--gold)" }} />
                <div>
                  <p className="text-sm font-semibold">Scor {googleProfile.soimiiScore}/10</p>
                  <p className="text-xs" style={{ color: "var(--text-muted)" }}>{googleProfile.soimiiReviews} recenzii · Șoimii Funerari</p>
                </div>
              </div>
              <div className="hidden sm:block w-px h-10" style={{ backgroundColor: "var(--warm-border)" }} />
              <div className="flex items-center gap-3">
                <Award className="w-5 h-5" style={{ color: "var(--gold)" }} />
                <div>
                  <p className="text-sm font-semibold">Premii 2023 · 2024 · 2025</p>
                  <p className="text-xs" style={{ color: "var(--text-muted)" }}>Excelență în servicii funerare</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="cta-section" data-testid="cta-section">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-5 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Sună Acum pentru Sprijin Imediat
          </h2>
          <p className="text-white/50 max-w-xl mx-auto mb-10 leading-relaxed">
            Suntem disponibili NON-STOP, 24 de ore din 24. Nu ezitați să ne contactați
            – suntem aici pentru a vă ajuta în orice moment.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={`tel:${companyInfo?.phone || ""}`}
              className="flex items-center gap-3 px-10 py-4 text-sm font-semibold tracking-wide no-underline transition-all hover:shadow-lg"
              style={{ borderRadius: "var(--radius-pill)", backgroundColor: "var(--gold)", color: "var(--black)" }}
              data-testid="cta-phone-btn"
            >
              <Phone className="w-4 h-4" />
              {companyInfo?.phone || ""}
            </a>
            <Link to="/contact" className="text-white/50 hover:text-white text-sm font-medium tracking-wide no-underline transition-colors">
              Sau trimite un mesaj →
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ Locations ═══ */}
      <section className="section-spacing-sm bg-white" id="contact-section" data-testid="locations-section">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(Array.isArray(companyInfo?.addresses) ? companyInfo.addresses : []).map((addr, i) => (
              <div key={i} className="flex items-start gap-4 p-6 rounded-2xl border" style={{ borderColor: "var(--warm-border)" }}>
                <MapPin className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: "var(--gold)" }} />
                <div>
                  <h3 className="text-base font-semibold mb-1">{addr.name}</h3>
                  <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{addr.street}, {addr.postalCode && `${addr.postalCode} `}{addr.city}</p>
                  <p className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>Program: NON-STOP 24/7</p>
                  {addr.mapLink && (
                    <a href={addr.mapLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold mt-3 no-underline" style={{ color: "var(--gold)" }}>
                      Obține Direcții →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
